// src/utils/incomeUtils.js
//
// Income-type aware amount calculations shared by the Income page and the
// Weekly Planner.
//
// Every income source has an `incomeType`:
//   • 'fixed'  (default) — the existing behaviour: `projectedAmount` is the
//     expected amount per paycheque.
//   • 'hourly' — variable/hourly income. The source carries an `hourly`
//     earnings profile and (optionally) per-pay-date scheduled hours, and the
//     projected amount per cheque is CALCULATED from wage × hours.
//
// Data model (all fields additive — older saved budgets simply lack them and
// behave exactly as before):
//
//   income.incomeType   'fixed' | 'hourly'            (default 'fixed')
//   income.hourly = {
//     rate,             // hourly wage
//     defaultHours,     // regular hours per pay period
//     overtimeRate,     // optional $/hr for overtime (defaults to rate × 1.5)
//     overtimeHours     // optional default OT hours per pay period
//     // Future earnings components (shift premiums, commissions, bonuses)
//     // belong in this object too, so the schema never needs a redesign:
//     // premiums: [...], commission: {...}, bonuses: [...]
//   }
//   income.payHours[]   // optional scheduled hours per pay date, parallel to
//                       // income.payDates. Entries may be a number (regular
//                       // hours) or { hours, overtimeHours } for full control.
//
// Backward compatibility: hourly sources STILL persist `projectedAmount` and
// the legacy `amount` field (as the calculated default per-cheque earnings),
// so every existing consumer — Home, Reports, Planner fallbacks, exports —
// keeps working without changes. Actual income continues to flow through the
// existing payActuals / actualAmount fields, which always override
// projections once entered.

import { parseAmount } from './formatters';

export const INCOME_TYPES = {
  FIXED: 'fixed',
  HOURLY: 'hourly'
};

/** Effective income type for a source ('fixed' unless explicitly hourly). */
export function getIncomeType(income) {
  return income?.incomeType === INCOME_TYPES.HOURLY
    ? INCOME_TYPES.HOURLY
    : INCOME_TYPES.FIXED;
}

/** True when the source calculates projections from wage × hours. */
export function isHourlyIncome(income) {
  return getIncomeType(income) === INCOME_TYPES.HOURLY;
}

/**
 * Normalized hourly profile with numeric fields.
 * overtimeRate defaults to 1.5 × rate when overtime hours are used but no
 * explicit OT rate was stored.
 */
export function getHourlyProfile(income) {
  const hourly = income?.hourly && typeof income.hourly === 'object' ? income.hourly : {};
  const rate = parseAmount(hourly.rate) || 0;
  const defaultHours = parseAmount(hourly.defaultHours) || 0;
  const overtimeHours = parseAmount(hourly.overtimeHours) || 0;
  const explicitOtRate = parseAmount(hourly.overtimeRate) || 0;
  const overtimeRate = explicitOtRate > 0 ? explicitOtRate : rate * 1.5;
  return { rate, defaultHours, overtimeRate, overtimeHours };
}

/**
 * Earnings for a given number of hours against a source's hourly profile.
 * `hoursEntry` may be a number (regular hours only) or
 * { hours, overtimeHours }; missing/blank falls back to the profile defaults.
 */
export function calculateHourlyEarnings(income, hoursEntry) {
  const profile = getHourlyProfile(income);

  let regularHours = profile.defaultHours;
  let overtimeHours = profile.overtimeHours;

  if (hoursEntry !== undefined && hoursEntry !== null && hoursEntry !== '') {
    if (typeof hoursEntry === 'object') {
      if (hoursEntry.hours !== undefined && hoursEntry.hours !== '') {
        regularHours = parseAmount(hoursEntry.hours) || 0;
      }
      if (hoursEntry.overtimeHours !== undefined && hoursEntry.overtimeHours !== '') {
        overtimeHours = parseAmount(hoursEntry.overtimeHours) || 0;
      }
    } else {
      regularHours = parseAmount(hoursEntry) || 0;
    }
  }

  return regularHours * profile.rate + overtimeHours * profile.overtimeRate;
}

/**
 * Projected earnings per paycheque for a source, before any per-date
 * schedule is applied. Fixed sources return their stored projectedAmount.
 */
export function getProjectedPerCheck(income) {
  if (isHourlyIncome(income)) {
    return calculateHourlyEarnings(income);
  }
  // `||` (not `??`) matches the app's long-standing fallback: a falsy
  // projectedAmount defers to the legacy `amount` field.
  return parseAmount(income?.projectedAmount || income?.amount) || 0;
}

/**
 * Projected earnings for one specific pay date (by index into payDates).
 * Hourly sources use the scheduled hours for that date (payHours[index])
 * when present, falling back to the profile defaults. Fixed sources always
 * project the same amount per cheque.
 */
export function getProjectedAmountForPayDate(income, payIndex) {
  if (isHourlyIncome(income)) {
    const payHours = Array.isArray(income?.payHours) ? income.payHours : [];
    return calculateHourlyEarnings(income, payHours[payIndex]);
  }
  return getProjectedPerCheck(income);
}

/**
 * Sum of projected earnings across all entered pay dates. Used when pay
 * dates exist (month-aware projection); callers without pay dates should
 * multiply getProjectedPerCheck by the frequency factor as before.
 */
export function getProjectedTotalForPayDates(income) {
  const payDates = Array.isArray(income?.payDates) ? income.payDates : [];
  return payDates.reduce(
    (sum, _date, i) => sum + getProjectedAmountForPayDate(income, i),
    0
  );
}

/**
 * True when the source's income for the month is still (partly) an estimate:
 * an hourly source where at least one pay date has no actual amount entered
 * yet. Once every cheque has an actual, the projection is fully replaced.
 */
export function hasEstimatedIncome(income) {
  if (!isHourlyIncome(income)) return false;
  const payDates = Array.isArray(income?.payDates) ? income.payDates : [];
  const payActuals = Array.isArray(income?.payActuals) ? income.payActuals : [];
  if (payDates.length === 0) {
    // No dated cheques: estimated until an overall actual is recorded.
    return !(parseAmount(income?.actualAmount) > 0);
  }
  return payDates.some((_d, i) => {
    const actual = payActuals[i];
    return actual === undefined || actual === null || actual === '';
  });
}
